import React, { useState, useRef } from "react";
import { getFrom16 } from "../../api/employeeApi";
import "./Form16View.css";
import "../../css/CustomTable.css";

const Form16View = () => {
  const [financialYear, setFinancialYear] = useState("2024-25"); // assessment year 
  const [form16Data, setForm16Data] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [totalDeduction, setTotalDeduction] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);

  const [taxSlabData, setTaxSlabData] = useState(null);
  const [taxSettingData, setTaxSettingData] = useState(null);

  const [taxAmount, setTaxAmount] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [cessAmount, setCessAmount] = useState(0);    // education cess
  const [cessPercent, setCessPercent] = useState(0);  // from settings
  const [totalTaxPayable, setTotalTaxPayable] = useState(0); // tax + cess

  const employeeCode = sessionStorage.getItem("username");
  const componentRef = useRef(); // ref to print area
  

  const financialYears = ["2024-25", "2025-26", "2026-27"]; // assessment year

  // Converts "2024-25" to details
  const getFinancialYearData = (fyStr) => {
    const [startYearStr] = fyStr.split("-");
    const startYear = parseInt(startYearStr);
    const endYear = startYear + 1;
    return {
      assessmentYear: `${startYear}-${endYear}`,
      fromDate: `01/04/${startYear}`,
      toDate: `31/03/${endYear}`,
    };
  };

  const { assessmentYear, fromDate, toDate } = getFinancialYearData(financialYear);

   const getBackendYear = (fyStr) => {
    const [startYearStr] = fyStr.split("-");
    const startYear = parseInt(startYearStr);
    return `${startYear - 1}${startYear}`;
  };

  const calculateTaxFromSlabs = (income, slabs) => {
  if (!slabs || slabs.length === 0 || !income || income <= 0) return 0;

  let tax = 0;
  const sortedSlabs = [...slabs].sort((a, b) => a.fromAmount - b.fromAmount);

  for (const slab of sortedSlabs) {
    if (income > slab.toAmount) {
      tax += (slab.toAmount - slab.fromAmount) * (slab.percentage / 100);
    } else if (income > slab.fromAmount) {
      tax += (income - slab.fromAmount) * (slab.percentage / 100);
      break;
    }
  }

  return tax;
};



  const backendYear = getBackendYear(financialYear);

  console.log(`backendYear :: ${backendYear}`)

  const handleFetchData = async () => {
  if (!employeeCode) {
    alert("Employee code not found in session!");
    return;
  }

  setLoading(true);
  setError("");
  setForm16Data(null);

  try {
    const data = await getFrom16(employeeCode, financialYear);
    setForm16Data(data.employeeDetails);
    setTaxSlabData(data.taxSlabs);
    setTaxSettingData(data.taxSettings);

    const deductionSum =
      data.employeeDetails.deductions?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;

    const incomeTotal =
      data.employeeDetails.employeeDetails.totalAllowance - deductionSum;

    // Update state so React re-renders
    setTotalDeduction(deductionSum);
    setTotalIncome(incomeTotal);

     // Calculate tax based only on slabs
    const slabTax = calculateTaxFromSlabs(incomeTotal, data.taxSlabs);
    setTaxAmount(slabTax);

    // 2 Get Education Cess % from settings
    const cessPercentSetting =
      data.taxSettings?.find(s => s.settingName === "EducationCessPercent")?.settingValue || 0;
    setCessPercent(cessPercentSetting);

    // 3Calculate cess (separately)
    const cess = (slabTax * cessPercentSetting) / 100;
    setCessAmount(cess);

    // 4 Calculate total payable (Tax + Cess)
    setTotalTaxPayable(slabTax + cess);

  } catch (err) {
    console.error("Error fetching Form 16:", err.message);
    setError(err.message || "Something went wrong while fetching Form 16 data.");
  } finally {
    setLoading(false);
  }
};


const totalSection10 = form16Data?.allowances
  ?.filter(a => a.form16Section === "10")
  ?.reduce((sum, a) => sum + (a.totalAmount || 0), 0) || 0;

const totalSection16 = form16Data?.allowances
  ?.filter(a => a.form16Section === "16")
  ?.reduce((sum, a) => sum + (a.totalAmount || 0), 0) || 0;

// Calculate totals
const totalSectionDeductions =
  form16Data?.deductions?.reduce((sum, x) => sum + (x.amount || 0), 0) || 0;

const totalDeductionIncludePF =
  totalSectionDeductions + (form16Data?.employeeDetails?.totalPF || 0);


  return (
    <div className="container mt-4">
      <h3 className="text-center mb-4 fw-bold text-uppercase">Form 16 - Tax Certificate</h3>

      {/* Input section */}
      <div className="row mb-3">
        <div className="col-md-4">
          <label className="form-label fw-semibold">Assessment Year</label>
          <select
            className="form-select"
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
          >
            {financialYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-4 d-flex align-items-end">
          <button
            className="btn btn-primary w-100"
            onClick={handleFetchData}
            disabled={loading}
          >
            {loading ? "Loading..." : "Fetch Form 16"}
          </button>
          
        </div>

        <div className="col-md-4 d-flex align-items-end">
          <button
            className="btn btn-success w-100"
            onClick={() => window.print()}
            disabled={!form16Data} // optional: disable if data not fetched
          >
            🖨️ Print Form 16
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {/* Printable Area */}
      {form16Data && (
        <div ref={componentRef} id="form16-print-area" className="p-4 border">
          {/* Header */}
          <div className="text-center">
            <h5 className="fw-bold text-uppercase" style={{ marginBottom: "4px", marginTop: "0" }}>
              FORM NO. 16
            </h5>
            <p style={{ marginBottom: "4px", marginTop: "4px" }}>[See rule 31(1)(a)]</p>
            <h6 className="text-uppercase" style={{ marginTop: "4px", marginBottom: "0" }}>
              Certificate under section 203 of the Income-tax Act, 1961 for Tax deducted at source on Salary
            </h6>
          </div>

          <hr />

          {/* Basic Details Table (Compact Layout) */}
          {/* <table className="table table-bordered mt-3 align-middle"> */}
          <table className="allowance-table">
            <thead className="table-light text-center">
              <tr>
                <th colSpan="2">Name and address of employer</th>
                <th colSpan="2">Name and designation of the employee</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan="2">
                  <strong>{form16Data.employeeDetails.clientName}</strong><br />
                  {form16Data.employeeDetails.clientAddress}
                </td>
                <td colSpan="2">
                  <strong>{form16Data.employeeDetails.employeeName}</strong><br />
                  {form16Data.employeeDetails.designationName}
                </td>
              </tr>

              <tr>
                <th>PAN of the Deductor</th>
                <th>TAN of the Deductor</th>
                <th>PAN of the Employee</th>
                <th>Employee Ref. No</th>
              </tr>

              <tr>
                <td>{form16Data.employeeDetails.clientPAN}</td>
                <td>{form16Data.employeeDetails.tan}</td>
                <td>{form16Data.employeeDetails.employeePAN}</td>
                <td>{form16Data.employeeDetails.employeeCode}</td>
              </tr>
            </tbody>
          </table>

          {/* CIT & Period Info */}  
          {/* <table className="table table-bordered align-middle text-center mt-3"> */}
          <table className="allowance-table">
            <thead className="table-light">
              {/* Top header row */}
              <tr>
                <th colSpan="4">CIT (TDS)</th>
                <th>Assessment Year <br />{financialYear}</th>
                <th colSpan="2">Period with the Employer</th>
              </tr>
          
              {/* Sub-header row */}
              <tr>
                <th colSpan="4"></th> {/* empty under CIT */}
                <th></th> {/* empty under Assessment Year */}
                <th>From Date</th>
                <th>To Date</th>
              </tr>
            </thead>
          
            <tbody>
              <tr>
                <td colSpan="4" className="text-start">
                  <strong>Address:</strong> The Commissioner of Income Tax (TDS), 4th Floor, A Wing,<br />
                  PMT Commercial Complex, Shankar Sheth Road, Swargate, Pune<br />
                  <strong>City:</strong> Pune &nbsp;&nbsp;&nbsp;&nbsp;
                  <strong>Pin Code:</strong> 400002
                </td>
                <td>{assessmentYear}</td>
                <td>{fromDate}</td>
                <td>{toDate}</td>
              </tr>
            </tbody>
          </table>
          
          {/* Quarterly Summary */}
          <h6 className="mt-4 fw-bold">
            Summary of amount paid/credited and tax deducted at source thereon in respect of the employee
          </h6>
          {/* <table className="table table-bordered text-center mt-2"> */}
          <table className="allowance-table">
            <thead className="table-light">
              <tr>
                <th>Quarter(s)</th>
                <th>Receipt Numbers of original quarterly statements of TDS under sub-section (3) of section 200</th>
                <th>Amount Paid/Credited (Rs)</th>
                <th>Tax Deducted (Rs)</th>
                <th>Tax Deposited/Remitted (Rs)</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Quarter 1</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
              <tr><td>Quarter 2</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
              <tr><td>Quarter 3</td><td>0</td><td>0</td><td>0</td><td>0</td></tr>
              <tr>
                <td>Quarter 4</td>
                <td colSpan="1">Not Available as the las Quarterly Statement is yet to be furnished</td>
                <td>-</td><td>-</td><td>-</td>
              </tr>
              <tr className="fw-bold">
                <td>Total</td><td></td><td>0</td><td>0</td><td>0</td>
              </tr>
            </tbody>
          </table>
         {/* <h6  style={{ marginTop: "4px", marginBottom: "0" }}> */}
         <h6>I. DETAILS OF TAX DEDUCTED AND DEPOSITED IN THE CENTRAL GOVERNMENT ACCOUNT THROUGH BOOK ADJUSTMENT</h6> <hr/>
         <h6>(The deductor to provide payment wise details of tax deducted and deposited with respect to the deductee)</h6>
          
          {/* <table className="table table-bordered text-center mt-2"> */}
          <table className="allowance-table">
            <thead className="table-light">
              <tr>
                <th>Sr.No.</th>
                <th>Tax Deposited in respect on of the deductee (Rs.)</th>
                <th colSpan="4">Book identification number (BIN)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td></td>
                <td colSpan="1"></td>
                <td>Receipt numbers of form No. 24G</td>
                <td>DDO Sequence Number in Form No. 24G</td>
                <td>Date of Transfer Voucher (dd/mm/yyyy)</td>
                <td>Status of matching with Form No.24G</td>
              </tr>
              <tr className="fw-bold">
                <td>Total</td><td>0</td><td></td><td></td><td></td><td></td>
              </tr>
            </tbody>
          </table>
          <h6>II. DETAILS OF TAX DEDUCTED AND DEPOSITED IN THE CENTRAL GOVERNMENT ACCOUNT THROUGH CHALLAN</h6><hr/>
          <h6>(The deductor to provide payment wise details of tax deducted and deposited with respect to the deductee)</h6>
          
          <table className="table table-bordered text-center mt-2">
          {/* <table className="allowance-table"> */}
            <thead className="table-light">
              <tr>
                <th>Sr.No.</th>
                <th>Tax Deposited in respect on of the employee (Rs.)</th>
                <th colSpan="4">Challan identification number (CIN)</th>                
              </tr>
            </thead>
            <tbody>
              <tr>
                <td></td>
                <td colSpan="1"></td>
                <td>BSR Code of the Bank Branch</td>
                <td>Date on which tax deposited(dd/mm/yyyy)</td>
                <td>Challan Serial Number</td>
                <td>Status of matching with OLTAS</td>
              </tr>
              <tr className="fw-bold">
                <td>Total</td><td>0</td><td></td><td></td><td></td><td></td>
              </tr>
            </tbody>
          </table>

          <table className="table table-bordered text-center mt-2">
          {/* <table className="allowance-table"> */}
            <thead className="table-light">
              <tr>
                <th>Verification</th>             
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>I, {form16Data.employeeDetails.employerName}, son/daughter of {form16Data.employeeDetails.employerName.split(" ").slice(1).join(" ")} working in the capacity
                    of (designation) do hereby certify that a sum of Rs 0.00(Zero Only) has been deductedat source and
                    paid to the credit of the Central Government. I further certify that the information given above is true 
                    and correct based on the books of account, documents and other available records.
                </td>
              </tr>
            </tbody>
          </table>
          <hr/>
          <p>Place</p>
          <p>Date</p>

          <div className="d-flex justify-content-between">
            <span>Designation: {form16Data.employeeDetails.designation}</span>
            <div style={{ textAlign: "center" }}>
              <p style={{ fontStyle: "italic", marginBottom: "2px" }}>
                (Signature of person responsible for deduction of tax)
              </p>
              <span><strong>Full Name: </strong>{form16Data.employeeDetails.employerName}</span>
            </div>
          </div>

{/* Notes Section */}
<div>
  {/* <p style={{ fontWeight: "bold", marginBottom: "4px" }}>Notes:</p> */}
  <p>Notes:</p>
  <ol>
    <li>
      1.Government deductors to fill information in item I if tax is paid without production of an
      income-tax challan and in item II if tax is paid accompanied by an income-tax challan.
    </li>
    <li>
      2.Non-Government deductors to fill information in item II.
    </li>
    <li>
      3.The deductor shall furnish the address of the Commissioner of Income-tax (TDS) having
      jurisdiction as regards TDS statements of the assessee.
    </li>
    <li>
      4.If an assessee is employed under one employer only during the year, certificate in Form No. 16
      issued for the quarter ending on 31st March of the financial year shall contain the details of
      tax deducted and deposited for all the quarters of the financial year.
    </li>
    <li>
      5.If an assessee is employed under more than one employer during the year, each of the employers
      shall issue Part A of the certificate in Form No. 16 pertaining to the period for which such
      assessee was employed with each of the employers. Part B (Annexure) of the certificate in Form
      No. 16 may be issued by each of the employers or the last employer at the option of the
      assessee.
    </li>
    <li>
      6.In items I and II, in the column for tax deposited in respect of the deductee, furnish the total
      amount of TDS and education cess.
    </li>
  </ol>
</div>

    <div className="container mt-4" style={{ maxWidth: "900px" }}>

      {/* Table - Gross Salary */}
      <div className="part-b-section">
      <table className="table table-bordered small">
      {/* <table className="table table-bordered text-center"> */}
        <thead className="table-light">
          <tr><th colSpan={5}>PART B (Annexure)</th></tr>
          <tr><th colSpan={5}>Details of Salary paid and any other income and tax deducted</th></tr>
          <tr>
            <th>1.</th>
            <th colSpan={2}>Gross Salary</th>
            {/* <th></th> */}
            <th>Rs</th>
            <th>Rs</th>
          </tr>
          
        </thead>
        <tbody>
          <tr>
            <td>(a)</td>
            <td colSpan={2}> Salary as per provisions contained in section 17(1)</td>
            {/* <td></td> */}
            <td>{form16Data.employeeDetails.totalAllowance || 0}</td>
            <td className="text-end">{ "0"}</td>
          </tr>
          <tr>
            <td>(b)</td>
            <td colSpan={2}>Value of perquisites under section 17(2) (as per Form No. 12BA, wherever applicable)</td>
            <td>{"0"}</td>
            <td className="text-end">{"0"}</td>
          </tr>
          <tr>
            <td>(c)</td>
            <td colSpan={2}>Profits in lieu of salary under section 17(3) (as per Form No. 12BA, wherever applicable)</td>
            <td>0</td>
            <td className="text-end">{"0"}</td>
          </tr>
          <tr className="fw-bold">
            <td>(d)</td>
            <td colSpan={3}><strong>Total</strong></td>
            <td className="text-end">{form16Data.employeeDetails.totalAllowance || 0}</td>
          </tr>
          {/* <tr>
            <th>2.</th>
            <td colSpan={2}>Less: Allowance to the extent exempt under section 10</td>
            <td>0</td>
            <td className="text-end"></td>
          </tr> */}
          {/* {form16Data?.allowances
            ?.filter(a => a.form16Section === "10")
            ?.map((a, index) => (
              <tr key={a.allowanceID}>
                <td>({String.fromCharCode(97 + index)})</td>
                <td colSpan={2}>{a.allowanceName}</td>
                <td>{a.totalAmount?.toFixed(2)}</td>
                <td></td>
              </tr>
          ))} */}


          {form16Data?.allowances?.some(a => a.form16Section === "10") && (
            <>
              <tr>
                <th>2.</th>
                <td colSpan={2}>Less: Allowance to the extent exempt under section 10</td>
                <td></td>
                <td className="text-end"></td>
              </tr>

              {form16Data.allowances
                .filter(a => a.form16Section === "10")
                .map((a, index) => (
                  <tr key={a.allowanceID}>
                    <td>({String.fromCharCode(97 + index)})</td>
                    <td colSpan={2}>{a.allowanceName}</td>
                    <td>{a.totalAmount?.toFixed(2)}</td>
                    <td></td>
                  </tr>
                ))}

              <tr>
                <td></td>
                <td colSpan={2}><b>Total (Section 10)</b></td>
                <td>
                  {form16Data.allowances
                    .filter(a => a.form16Section === "10")
                    .reduce((sum, a) => sum + (a.totalAmount || 0), 0)
                    .toFixed(2)}
                </td>
                <td></td>
              </tr>
            </>
          )}

          <tr>
            <th>3.</th>
            <td colSpan={2}>Balance (1 - 2)</td>
            <td></td>
            <td className="text-end">
              {(
                (form16Data?.employeeDetails?.totalAllowance || 0) - totalSection10
              ).toFixed(2)}
            </td>
          </tr>

          {form16Data?.allowances?.some(a => a.form16Section === "16") && (
            <>
              <tr>
                <th>4.</th>
                <td colSpan={2}>Less: Deductions under section 16</td>
                <td></td>
                <td className="text-end"></td>
              </tr>

              {form16Data.allowances
                .filter(a => a.form16Section === "16")
                .map((a, index) => (
                  <tr key={a.allowanceID}>
                    <td>({String.fromCharCode(97 + index)})</td>
                    <td colSpan={2}>{a.allowanceName}</td>
                    <td>{a.totalAmount?.toFixed(2)}</td>
                    <td></td>
                  </tr>
                ))}
            </>
          )}

          <tr>
            <th>5.</th>
            <td colSpan={2}> Aggregate of 4 (section 16)</td>
            <td>{totalSection16}</td>
            <td className="text-end"></td>
          </tr>

          <tr>
            <th>6.</th>
            <td colSpan={2}> Income chargeable under the head "Salaries (3 - 5)</td>
            <td></td>
            <td>{(
                (form16Data?.employeeDetails?.totalAllowance || 0) - totalSection10 - totalSection16
              ).toFixed(2)}
            </td>
            
          </tr>
          <tr>
            <th>7.</th>
            <td colSpan={2}>  Add:Any other income reported by the employee</td>
            <td></td>
            <td>0</td>
          </tr>
          <tr>
            <th>8.</th>
            <td colSpan={2}>  Gross total income(6 + 7)</td>
            <td></td>
            <td>{(
                (form16Data?.employeeDetails?.totalAllowance || 0) - totalSection10 - totalSection16
              ).toFixed(2)}
            </td>
          </tr>
          <tr>
            <th>9.</th>
            <td colSpan={2}> Deduction under Chapter VIA Section 80C, 80CCC AND 80CCD</td>
            <td>Gross Amount</td>
            <td>Deductible Amount</td>
          </tr>
          <tr>
            <td>(a)</td>
            <td colSpan={2}> Section 80C</td>
            <td></td>
            <td>
              {(
                (form16Data?.deductions
                  ?.filter(x => x.sectionCode === "80C")
                  ?.reduce((sum, x) => sum + x.amount, 0) || 0
                )
                +
                (form16Data?.employeeDetails?.totalPF || 0)
              ).toFixed(2)}
            </td>
          </tr>
          <tr>
            <td>(b)</td>
            <td colSpan={2}> Section 80D</td>
            <td></td>
            {/* <td>{form16Data?.deductions?.find(x => x.sectionCode === "80D")?.amount || 0}</td> */}
            <td>
              {
                form16Data?.deductions
                  ?.filter(x => x.sectionCode === "80D")     // get only 80D items
                  ?.reduce((sum, x) => sum + x.amount, 0)    // sum all their amounts
                || 0
              }
            </td>
          </tr>
          <tr>
            <td>(c)</td>
            <td colSpan={2}> Section 80E</td>
            <td></td>
            {/* <td>{form16Data?.deductions?.find(x => x.sectionCode === "80E")?.amount || 0}</td> */}
            <td>
              {
                form16Data?.deductions
                  ?.filter(x => x.sectionCode === "80E")     // get only 80E items
                  ?.reduce((sum, x) => sum + x.amount, 0)    // sum all their amounts
                || 0
              }
            </td>
          </tr>
          <tr>
            <td>(d)</td>
            <td colSpan={2}> Section 80G</td>
            <td></td>
            {/* <td>{form16Data?.deductions?.find(x => x.sectionCode === "80G")?.amount || 0}</td> */}
            <td>
              {
                form16Data?.deductions
                  ?.filter(x => x.sectionCode === "80G")     // get only 80G items
                  ?.reduce((sum, x) => sum + x.amount, 0)    // sum all their amounts
                || 0
              }
            </td>
          </tr>
          <tr>
            <td>(e)</td>
            <td colSpan={2}> Section 80T</td>
            <td></td>
            <td>
              {
                form16Data?.deductions
                  ?.filter(x => x.sectionCode === "80T")     // get only 80T items
                  ?.reduce((sum, x) => sum + x.amount, 0)    // sum all their amounts
                || 0
              }
            </td>
          </tr>
          <tr>
            <td>(e)</td>
            <td colSpan={2}> Section 80U</td>
            <td></td>
            <td>
              {
                form16Data?.deductions
                  ?.filter(x => x.sectionCode === "80U")     // get only 80U items
                  ?.reduce((sum, x) => sum + x.amount, 0)    // sum all their amounts
                || 0
              }
            </td>
          </tr>
          <tr>
            <td>(e)</td>
            <td colSpan={2}> Section ETC</td>
            <td></td>
            
            <td>
              {
                form16Data?.deductions
                  ?.filter(x => x.sectionCode === "ETC")     // get only ETC items
                  ?.reduce((sum, x) => sum + x.amount, 0)    // sum all their amounts
                || 0
              }
            </td>
          </tr>
          
          <tr>
            <th>10.</th>
            <td> Aggregate of deductible amount under Chapter VIA</td>
            <td>Gross Amount</td>
            <td>Qualifying Amount</td>
            <td>Deductible Amount</td>
          </tr>
          <tr>
            <th></th>
            <td></td>
            <td></td>
            <td>{totalDeductionIncludePF || 0}</td>
            <td>{totalDeductionIncludePF || 0}</td>
          </tr>
          <tr>
            <th>11.</th>
            <td colSpan={2}> Total income(8 - 10)</td>
            <td></td>
            <td>{(
                (form16Data?.employeeDetails?.totalAllowance || 0) - totalSection10 - totalSection16 - totalDeductionIncludePF 
              ).toFixed(2)}
            </td>
          </tr>
          <tr>
            <th>12.</th>
            <td colSpan={2}> Tax on Total Income </td>
            <td></td>
            <td>{taxAmount.toFixed(2)}</td>
          </tr>
          <tr>
            <th>13.</th>
            <td colSpan={2}> Education Cess @ {cessPercent}% (on tax computed at S. No. 12)</td>
            <td></td>
            <td>{cessAmount.toFixed(2)}</td>
          </tr>

          <tr>
            <th>14.</th>
            <td colSpan={2}> Total Tax Payable (12 + 13)</td>
            <td></td>
            <td>{totalTaxPayable.toFixed(2)}</td>
          </tr>
          <tr>
            <th>15.</th>
            <td colSpan={2}> Relief under section 89(attach details)</td>
            <td></td>
            <td>0.00</td>
          </tr>
          <tr>
            <th>16.</th>
            <td colSpan={2}> Tax payable(14 - 15)</td>
            <td></td>
            <td>{(totalTaxPayable - 0).toFixed(2)}</td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

          <table className="table table-bordered text-center mt-2">
            <thead className="table-light">
              <tr>
                <th>Verification</th>             
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>I, {form16Data.employeeDetails.employerName}, son/daughter of {form16Data.employeeDetails.employerName.split(" ").slice(1).join(" ")} working in the capacity
                    of (designation) do hereby certify that a sum of Rs 0.00(Zero Only) has been deductedat source and
                    paid to the credit of the Central Government. I further certify that the information given above is true 
                    and correct based on the books of account, documents and other available records.
                </td>
              </tr>
            </tbody>
          </table>
          <hr/>
          <p>Place</p>
          <p>Date</p>

          <div className="d-flex justify-content-between">
            <span>Designation: {form16Data.employeeDetails.designation}</span>
            <div style={{ textAlign: "center" }}>
              <p style={{ fontStyle: "italic", marginBottom: "2px" }}>
                (Signature of person responsible for deduction of tax)
              </p>
              <span><strong>Full Name: </strong>{form16Data.employeeDetails.employerName}</span>
            </div>
          </div>
        
          <div className="text-center mt-3">
            <small className="text-muted">Generated automatically by AIM Payroll System</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default Form16View;
